
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import _Link from 'next/link'
import { Heading as _Heading } from '@/components/Heading'
import { DocumentationLayout as _Layout } from '@/layouts/DocumentationLayout'
import { ContentsLayout as _Default } from '@/layouts/ContentsLayout'
const Layout = _Layout;
const meta = {
  "id": "negotiations",
  "title": "Setting Up Negotiations",
  "sidebar_label": "Negotiations"
};
const slug = 'negotiations';
const tableOfContents = [{
  "title": "Step 1: Setting Up Negotiation Blocks",
  "slug": "step-1-setting-up-negotiation-blocks",
  "children": []
}, {
  "title": "Step 2: How the Negotiation Works",
  "slug": "step-2-how-the-negotiation-works",
  "children": [{
    "title": "For Buyers:",
    "slug": "for-buyers",
    "children": []
  }, {
    "title": "Viewing Submitted Offers:",
    "slug": "viewing-submitted-offers",
    "children": []
  }, {
    "title": "For Sellers:",
    "slug": "for-sellers",
    "children": []
  }, {
    "title": "Accepting an Offer:",
    "slug": "accepting-an-offer",
    "children": []
  }, {
    "title": "For Buyers After Acceptance:",
    "slug": "for-buyers-after-acceptance",
    "children": []
  }, {
    "title": "Checkout:",
    "slug": "checkout",
    "children": []
  }]
}];

const layoutProps = {
  Layout,
meta,
slug,
tableOfContents
};
const MDXLayout = _Default
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">






    <p>{`In this guide, we’ll explain how Tradly’s negotiation feature works and how to set it up for your marketplace.`}</p>
    <_Heading level={2} id={"step-1-setting-up-negotiation-blocks"} nextElement={{
      "type": "paragraph"
    }}>Step 1: Setting Up Negotiation Blocks</_Heading>
    <p>{`To enable negotiation on a listing, you need to configure two specific blocks from the editor:`}</p>
    <ul {...{
      "role": "list"
    }}>
      <li parentName="ul"><strong parentName="li">{`CTA - Negotiate`}</strong></li>
      <li parentName="ul"><strong parentName="li">{`Seller Negotiation List`}</strong></li>
    </ul>
    <p>{`To set these up:`}</p>
    <ul {...{
      "role": "list"
    }}>
      <li parentName="ul">{`Go to `}<strong parentName="li">{`Editor > Listing`}</strong>{`.`}</li>
      <li parentName="ul">{`Select any row where you want to enable negotiations and add the two blocks mentioned above.`}</li>
    </ul>
    <img src="/img/negotiations-blocks.png" alt="notification-center" className="w-full h-full object-cover rounded-lg shadow-lg" />
    <_Heading level={2} id={"step-2-how-the-negotiation-works"} nextElement={{
      "type": "paragraph"
    }}>Step 2: How the Negotiation Works</_Heading>
    <p>{`Once you’ve set up the negotiation blocks, here’s how the feature will work:`}</p>
    <_Heading level={3} id={"for-buyers"} nextElement={{
      "type": "list",
      "ordered": false,
      "start": null,
      "spread": true
    }}>For Buyers:</_Heading>
    <ul {...{
      "role": "list"
    }}>
      <li parentName="ul">
        <p parentName="li">{`When a buyer visits the listing details page, they will see two buttons: `}<strong parentName="p">{`Buy Now`}</strong>{` and `}<strong parentName="p">{`Negotiate`}</strong>{`.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The `}<strong parentName="p">{`Buy Now`}</strong>{` button allows buyers to purchase the product at the listed price.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`If the buyer wants to negotiate, they can click the `}<strong parentName="p">{`Negotiate`}</strong>{` button, which opens a popup displaying a message box and a price input field.`}</p>
        <img src="/img/negotiations-form.png" alt="notification-center" className="w-full h-full object-cover rounded-lg shadow-lg" />
      </li>
      <li parentName="ul">
        <p parentName="li">{`The buyer can enter their desired price and a message, then submit their offer.`}</p>
        <img src="/img/negotiations-submitted-offer.png" alt="notification-center" className="w-full h-full object-cover rounded-lg shadow-lg" />
      </li>
    </ul>
    <_Heading level={3} id={"viewing-submitted-offers"} nextElement={{
      "type": "list",
      "ordered": false,
      "start": null,
      "spread": false
    }}>Viewing Submitted Offers:</_Heading>
    <ul {...{
      "role": "list"
    }}>
      <li parentName="ul">{`After the offer is submitted, buyers can track their offer in the `}<strong parentName="li">{`Negotiation`}</strong>{` section of the listing page.`}</li>
    </ul>
    <_Heading level={3} id={"for-sellers"} nextElement={{
      "type": "list",
      "ordered": false,
      "start": null,
      "spread": false
    }}>For Sellers:</_Heading>
    <ul {...{
      "role": "list"
    }}>
      <li parentName="ul">{`Sellers can log in, go to their listing, and view the list of submitted offers in the `}<strong parentName="li">{`Seller Negotiation List`}</strong>{`.`}</li>
      <li parentName="ul">{`The seller can see how many people have submitted offers and view their proposed prices.`}</li>
      <li parentName="ul">{`By clicking `}<strong parentName="li">{`View All Offers`}</strong>{`, the seller will open a sidebar showing all the offers in detail.`}</li>
      <li parentName="ul">{`The seller can then review each offer individually and either `}<strong parentName="li">{`Accept`}</strong>{` or `}<strong parentName="li">{`Decline`}</strong>{` it.`}</li>
    </ul>
    <_Heading level={3} id={"accepting-an-offer"} nextElement={{
      "type": "list",
      "ordered": false,
      "start": null,
      "spread": false
    }}>Accepting an Offer:</_Heading>
    <ul {...{
      "role": "list"
    }}>
      <li parentName="ul">{`Once the seller accepts an offer, the price of the listing is updated to match the buyer’s proposed price.`}</li>
    </ul>
    <img src="/img/negotiations-list.png" alt="notification-center" className="w-full h-full object-cover rounded-lg shadow-lg" />
    <img src="/img/negotiations-seller-accept.png" alt="notification-center" className="w-full h-full object-cover rounded-lg shadow-lg" />
 
    <_Heading level={3} id={"for-buyers-after-acceptance"} nextElement={{
      "type": "list",
      "ordered": false,
      "start": null,
      "spread": false
    }}>For Buyers After Acceptance:</_Heading>
    <ul {...{
      "role": "list"
    }}>
      <li parentName="ul">{`When the buyer logs back in, they will see the updated price on the listing.`}</li>
      <li parentName="ul">{`If they click the `}<strong parentName="li">{`Negotiate`}</strong>{` button again, the sidebar will show that their offer has been accepted.`}</li>
      <li parentName="ul">{`The buyer can now proceed by clicking `}<strong parentName="li">{`Buy Now`}</strong>{`, which will add the product to their checkout page at the negotiated price.`}
        <img src="/img/negotiations-accept-offer.png" alt="notification-center" className="w-full h-full object-cover rounded-lg shadow-lg" />
      </li>
    </ul>
    <_Heading level={3} id={"checkout"} nextElement={{
      "type": "list",
      "ordered": false,
      "start": null,
      "spread": false
    }}>Checkout:</_Heading>
    <ul {...{
      "role": "list"
    }}>
      <li parentName="ul">{`On the checkout page, the buyer can complete the purchase at the agreed-upon price.`}
        <img src="/img/negotiations-checkout.png" alt="notification-center" className="w-full h-full object-cover rounded-lg shadow-lg" />
      </li>
    </ul>
    <hr></hr>
    <p>{`This setup provides a flexible negotiation process for both buyers and sellers, making it easy to manage offers and agree on a fair price.`}</p>



    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
MDXContent.layoutProps = layoutProps
